import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { ArrowBack, PhotoLibrary, Refresh } from '@mui/icons-material'
import { Alert, Box, Button, Card, CardContent, Skeleton, Stack, TextField, Typography } from '@mui/material'
import Player from '@vimeo/player'

import { FtueVideo, FtueVideoThumbnail } from '../../../api/core'
import FtueVideoPlayer from '../../../features/ftue-videos/components/FtueVideoPlayer/FtueVideoPlayer'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { useGetOnboardingVideoQuery, useUpdateOnboardingVideoMutation } from '../../api/onboarding-videos'
import { useLazyGetVimeoOEmbedQuery } from '../../api/vimeo'
import { DiscardChangesDialog } from '../../components/DiscardChangesDialog/DiscardChangesDialog'
import { FtueChapterEditor } from '../../components/FtueChapterEditor/FtueChapterEditor'
import { InputTranslator } from '../../components/InputTranslator/InputTranslator'
import { VimeoPicturePickerDialog } from '../../components/VimeoPicturePickerDialog/VimeoPicturePickerDialog'
import {
  getUnstoredOnboardingVideoFields,
  onboardingVideoFieldLabelKeys,
  toOnboardingVideoFormValues,
  toOnboardingVideoUpdateBody,
} from '../../features/onboarding-videos/helpers/onboardingVideoHelpers'
import { OnboardingVideoFormValues } from '../../features/onboarding-videos/types/OnboardingVideo'
import { getValidationErrorMessage } from '../../helpers/apiValidationError'
import { getThumbnailPictureId, thumbnailsMatch, toVimeoThumbnails } from '../../helpers/vimeo'
import { useEditPageLeave } from '../../hooks/useEditPageLeave'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'
import { onboardingVideosPath } from './OnboardingVideosPage'

export const OnboardingVideoEditPage: FC = () => {
  const { videoId = '' } = useParams<{ videoId: string }>()
  usePage(pageService.getInternalPageWithId('internal-onboarding-video-edit'))
  const { t } = useTranslation()
  const { notify, notifyError } = useMutationFeedback()
  const navigate = useNavigate()
  const videoQuery = useGetOnboardingVideoQuery(videoId)
  const [updateVideo, { isLoading: isSaving }] = useUpdateOnboardingVideoMutation()
  const [loadOEmbed, { isFetching: isLoadingThumbnail }] = useLazyGetVimeoOEmbedQuery()
  const [player, setPlayer] = useState<Player>()
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isDirty },
  } = useForm<OnboardingVideoFormValues>({ defaultValues: toOnboardingVideoFormValues(undefined) })
  const values = useWatch({ control }) as OnboardingVideoFormValues
  const { guard, leave } = useEditPageLeave(isDirty)
  const video = videoQuery.data
  const loadedVideoId = useRef<string>()

  // Only on the first load of a video, so a refetch after saving can't replace edits that weren't stored.
  useEffect(() => {
    if (video && loadedVideoId.current !== video.id) {
      loadedVideoId.current = video.id
      reset(toOnboardingVideoFormValues(video))
    }
  }, [video, reset])

  const previewVideo = useMemo<FtueVideo | undefined>(
    () =>
      video
        ? {
            ...video,
            names: values.names,
            chapters: (values.chapters ?? []).map((chapter, i) => ({ ...chapter, chapterId: chapter.chapterId ?? -(i + 1) })),
          }
        : undefined,
    [video, values.names, values.chapters]
  )

  const applyPickedPicture = (thumbnails: FtueVideoThumbnail[]) => {
    setIsPickerOpen(false)
    setValue('thumbnails', thumbnails, { shouldDirty: true })
  }

  const applyCurrentVimeoThumbnail = async () => {
    if (!video) return
    try {
      const oembed = await loadOEmbed(video.vimeoId).unwrap()
      const thumbnails = toVimeoThumbnails(oembed.thumbnailUrl)
      if (thumbnailsMatch(thumbnails, values.thumbnails)) {
        notify(t('internal-csm:onboarding_videos_thumbnail_already_current'), 'info')
        return
      }
      setValue('thumbnails', thumbnails, { shouldDirty: true })
    } catch {
      notifyError(t('internal-csm:onboarding_videos_thumbnail_load_failed'))
    }
  }

  const save = handleSubmit(async (formValues) => {
    if (!video) return
    const body = toOnboardingVideoUpdateBody(video, formValues)
    const name = formValues.names.en || '---'
    let stored: FtueVideo | null | undefined
    let failure: unknown
    try {
      stored = await updateVideo(body).unwrap()
    } catch (error) {
      failure = error
      stored = await videoQuery
        .refetch()
        .unwrap()
        .catch(() => undefined)
    }
    const unstored = stored ? getUnstoredOnboardingVideoFields(body, stored) : undefined
    if (unstored && unstored.length === 0) {
      notify(
        t(failure ? 'internal-csm:onboarding_videos_saved_unconfirmed' : 'internal-csm:onboarding_videos_saved', { name }),
        failure ? 'warning' : 'success'
      )
      leave(onboardingVideosPath)
      return
    }
    if (failure) {
      const reason = getValidationErrorMessage(failure)
      notifyError(reason ? t('internal-csm:onboarding_videos_save_failed_reason', { reason }) : t('internal-csm:onboarding_videos_save_failed'))
      return
    }
    const fields = (unstored ?? []).map((field) => t(onboardingVideoFieldLabelKeys[field])).join(', ')
    notifyError(t('internal-csm:onboarding_videos_not_stored', { fields }))
  })

  const thumbnail = values.thumbnails?.[3] ?? values.thumbnails?.[0]
  const thumbnailChanged = !!video && getThumbnailPictureId(values.thumbnails?.[0]?.link) !== getThumbnailPictureId(video.thumbnails?.[0]?.link)
  const isMissing = videoQuery.isSuccess && video === null

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button startIcon={<ArrowBack />} onClick={() => navigate(onboardingVideosPath)}>
            {t('internal-common:back')}
          </Button>
          <Typography variant="h5" fontWeight="bold">
            {t('internal-csm:onboarding_videos_edit_title', { name: video?.names?.en || '' })}
          </Typography>
        </Stack>
        <Button variant="contained" onClick={save} disabled={!video || isSaving}>
          {t('internal-common:save')}
        </Button>
      </Stack>

      {videoQuery.isLoading ? (
        <Skeleton variant="rectangular" height={480} />
      ) : isMissing ? (
        <Typography color="error.main">{t('internal-csm:onboarding_videos_missing')}</Typography>
      ) : videoQuery.isError || !video || !previewVideo ? (
        <Typography color="error.main">{t('internal-csm:onboarding_videos_load_one_failed')}</Typography>
      ) : (
        <Stack spacing={3}>
          <Alert severity="info">{t('internal-csm:onboarding_videos_customer_note')}</Alert>
          <Box display="grid" gridTemplateColumns={{ xs: 'minmax(0, 1fr)', md: 'minmax(0, 7fr) minmax(0, 5fr)' }} gap={3} alignItems="start">
            <Box>
              <FtueVideoPlayer video={previewVideo} hideChapterList hideCloseButton onPlayerReady={setPlayer} />
            </Box>
            <Box>
              <Card>
                <CardContent>
                  <Stack spacing={2}>
                    <Controller
                      name="names"
                      control={control}
                      render={({ field }) => (
                        <InputTranslator
                          value={field.value}
                          onChange={field.onChange}
                          dialogTitle={t('internal-csm:onboarding_videos_title')}
                          label={t('internal-csm:onboarding_videos_title')}
                          disabled={isSaving}
                        />
                      )}
                    />
                    <Controller
                      name="videoName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          size="small"
                          label={t('internal-csm:onboarding_videos_saas_link')}
                          helperText={t('internal-csm:onboarding_videos_saas_link_help')}
                          disabled={isSaving}
                          fullWidth
                        />
                      )}
                    />
                    <Box>
                      <Typography variant="subtitle2" mb={1}>
                        {t('internal-csm:onboarding_videos_thumbnail')}
                      </Typography>
                      {thumbnail ? (
                        <Box
                          component="img"
                          src={thumbnail.link}
                          alt={t('internal-csm:onboarding_videos_thumbnail')}
                          sx={{ display: 'block', width: '100%' }}
                        />
                      ) : (
                        <Typography color="text.secondary">---</Typography>
                      )}
                      {thumbnailChanged && (
                        <Typography variant="body2" color="warning.main" mt={1}>
                          {t('internal-csm:onboarding_videos_thumbnail_changed')}
                        </Typography>
                      )}
                      <Stack direction="row" spacing={2} alignItems="center" mt={1} flexWrap="wrap">
                        <Button variant="outlined" startIcon={<Refresh />} onClick={applyCurrentVimeoThumbnail} disabled={isLoadingThumbnail || isSaving}>
                          {t('internal-csm:onboarding_videos_thumbnail_use_current')}
                        </Button>
                        <Button variant="outlined" startIcon={<PhotoLibrary />} onClick={() => setIsPickerOpen(true)} disabled={isSaving}>
                          {t('internal-csm:onboarding_videos_thumbnail_choose')}
                        </Button>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        {t('internal-csm:onboarding_videos_thumbnail_help')}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          </Box>
          <Card>
            <CardContent>
              <Controller
                name="chapters"
                control={control}
                render={({ field }) => <FtueChapterEditor chapters={field.value} onChange={field.onChange} player={player} disabled={isSaving} />}
              />
            </CardContent>
          </Card>
        </Stack>
      )}

      {video && (
        <VimeoPicturePickerDialog
          open={isPickerOpen}
          vimeoId={video.vimeoId}
          currentThumbnails={values.thumbnails}
          onClose={() => setIsPickerOpen(false)}
          onSelect={applyPickedPicture}
        />
      )}
      <DiscardChangesDialog
        guard={guard}
        title={t('internal-csm:onboarding_videos_discard_title')}
        text={t('internal-csm:onboarding_videos_discard_confirm')}
        discardText={t('internal-csm:onboarding_videos_discard')}
      />
    </Box>
  )
}
